import { Box, Link, Typography } from '@mui/material'
import moment from 'moment'
import { FC, useContext, useMemo } from 'react'
import { Link as NavLink } from 'react-router-dom'

// components import
import { CardBox } from '../../../theme/styleComponents'
import BillPreviewEntriesTable from './BillPreviewEntriesTable'
import BillQRCode from './BillQRCode'
import FundRequestPreviewEntriesTable from './FundRequestPreviewEntriesTable'
import OutstandingBillsTable from './OutstandingBillsTable'
import BillEntriesSubtotals from './BillEntriesSubtotals'
import ProformaNotes from './ProformaNotes'

// others
import { FINAL_BILL_STATUSES, PRE_BILL_STATUSES } from '../../../constants'
import {
  BillType,
  ExternalUser,
  ExternalUserCaseRole,
  FetchBillDetailsQuery,
  FetchPublicBillDetailsQuery,
  UserType
} from '../../../../generated'
import { images } from '../../../assets/images'
import { flexJustifySpaceBetween } from '../../../theme/styleConstants'
import { generateBillPaymentUrl } from '../../../utils/billing'
import BillPreviewSummary from './BillPreviewSummary'
import {
  CalculateClientBillDataResult,
  UseFetchBillInterestResult,
  UseFetchOutstandingBillsResult
} from '../../../interfaceTypes'
import { AuthContext } from '../../../context/AuthContext'

type ProtectedBill = NonNullable<FetchBillDetailsQuery['fetchBillDetails']['bill']>
type PublicBill = NonNullable<FetchPublicBillDetailsQuery['fetchPublicBillDetails']['bill']>
type Client = Pick<ExternalUser, 'id' | 'firstName' | 'lastName' | 'displayName' | 'clientNumber'>

/** Address fields returned for external user on bill details. */
interface BillClientAddress {
  address1?: string
  address2?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
}

/** Client from bill case party; includes addresses when loaded by fetchBillDetails. */
type BillCaseClient = Pick<ExternalUser, 'id' | 'firstName' | 'lastName' | 'displayName' | 'clientNumber'> & {
  addresses?: BillClientAddress[] | null
}

interface BillPreviewProps {
  bill?: ProtectedBill | PublicBill
  isFinalBill?: boolean
  client?: Client | null
  selectedAccessToken?: { token: string | undefined; amount: number | undefined }
  clientData?: CalculateClientBillDataResult
  interestData?: UseFetchBillInterestResult
  outstandingBillsData?: UseFetchOutstandingBillsResult
  isClientView?: boolean
  onReadyToPrint?: (isReady: boolean) => void
}

const BillPreview: FC<BillPreviewProps> = ({
  bill,
  client: clientProp,
  clientData: clientDataProp,
  interestData,
  selectedAccessToken,
  outstandingBillsData,
  isFinalBill,
  isClientView,
  onReadyToPrint
}) => {
  const { isLoggedIn } = useContext(AuthContext)

  const billData = useMemo(() => {
    const { dueDate, id, status, billItems, case: caseData } = bill || {}
    const { id: caseId, title, caseNumber, consultMatterNumber } = caseData || {}
    const isPrebill = status && PRE_BILL_STATUSES.includes(status)
    const responsibleAttorney = caseData?.caseToUsers?.find(user => user?.userType === UserType.ResponsibleAttorney_1)

    return {
      dueDate,
      id,
      status,
      billItems,
      isPrebill,
      title,
      caseId,
      caseNumber: caseNumber || consultMatterNumber,
      responsibleAttorney
    }
  }, [bill])

  const clientData = useMemo(() => {
    const getClientRole = () => {
      if (isFinalBill && clientProp?.id) {
        return bill?.case?.caseToExternalUserToExternalUserRole?.find(
          externalUserRole => externalUserRole.externalUserToExternalUserRole.externalUser?.id === clientProp?.id
        )
      }

      return bill?.case?.caseToExternalUserToExternalUserRole?.find(
        role => role.caseRole === ExternalUserCaseRole.PrimaryClient
      )
    }

    const clientRole = getClientRole()
    const client = clientRole?.externalUserToExternalUserRole?.externalUser as BillCaseClient | null
    let formattedAddress = ''
    if (clientRole?.billingAddress) {
      formattedAddress = clientRole.billingAddress
    } else {
      const firstAddress = client?.addresses?.[0]
      const addressParts = firstAddress
        ? [
            firstAddress.address1,
            firstAddress.address2,
            [firstAddress.city, firstAddress.state].filter(Boolean).join(', '),
            firstAddress.zipCode,
            firstAddress.country
          ].filter(Boolean)
        : []
      formattedAddress = addressParts.length ? addressParts.join(', ') : ''
    }

    return {
      splitShare: clientRole?.splitBillShare,
      number: client?.clientNumber ? `#${client.clientNumber}` : '',
      name: client?.displayName || (client?.firstName ? `${client.firstName} ${client.lastName || ''}` : ''),
      clientId: client?.id,
      address: formattedAddress
    }
  }, [bill?.case?.caseToExternalUserToExternalUserRole, isFinalBill, clientProp?.id])

  const { id, dueDate, billItems, status, isPrebill, responsibleAttorney, title, caseNumber, caseId } = billData
  const { number: clientNumber, name: clientName, address: clientAddress } = clientData

  const paymentPageUrl = selectedAccessToken?.token && generateBillPaymentUrl(selectedAccessToken?.token)
  const qrEnabled = process.env.REACT_APP_ENABLE_BILL_QR_CODE === 'true'
  const showQRCode = qrEnabled && isFinalBill && paymentPageUrl && status && FINAL_BILL_STATUSES.includes(status)

  return (
    <CardBox
      id="bill-preview"
      sx={{
        '@media print': {
          boxShadow: 'none !important',
          margin: '0 !important',
          width: '100% !important',
          minHeight: '100% !important',
          pageBreakAfter: 'always',
          '& *': {
            color: '#000 !important',
            fontFamily: 'inherit !important'
          }
        }
      }}
    >
      <Box
        sx={{
          px: 4,
          pt: 6,
          '@media print': {
            padding: '24px !important'
          }
        }}
      >
        <Box mb={5}>
          <Box sx={{ ...flexJustifySpaceBetween }}>
            <Box component="img" src={images.LOGO} alt="Helmer Legal Logo" sx={{ maxWidth: 120 }} />
            {bill?.type === BillType.Bill && status && (
              <Typography variant="h6" fontWeight={700} color="#0A2540">
                {isPrebill ? 'PRE BILL' : 'BILL'}
              </Typography>
            )}
            {bill?.type === BillType.Fund && (
              <Typography variant="h6" fontWeight={700} color="#0A2540">
                FUNDS REQUEST
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: { xs: 0.5, md: 1 }, display: 'inline-block', width: '100%' }}>
            <Box
              display="flex"
              flexDirection="column"
              gap={0.5}
              sx={{ width: { md: '50%', xs: '100%' }, float: { md: 'left', xs: 'none' } }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="#0A2540">
                Helmer, Conley & Kasselman, P.A.
              </Typography>
              <Typography fontSize="12px">111 White Horse Pike</Typography>
              <Typography fontSize="12px">Haddon Heights, NJ 08035</Typography>
              <Typography fontSize="12px">billpay@helmerlegal.com | (856) 547-7888</Typography>
              <Typography fontSize="12px">
                Responsible Attorney: {responsibleAttorney?.user?.firstName} {responsibleAttorney?.user?.lastName}
              </Typography>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              sx={{ mt: { xs: 4, md: 0 }, width: { md: '30%', xs: '100%' }, float: { md: 'right', xs: 'none' } }}
            >
              <Typography variant="subtitle1" fontWeight={700} textAlign="right" color="#0A2540" mb={0.5}>
                {clientName}
              </Typography>
              {clientAddress && (
                <Typography fontSize="12px" textAlign="right" color="#0A2540" mb={0.5} sx={{ whiteSpace: 'pre-line' }}>
                  {clientAddress}
                </Typography>
              )}
              {clientNumber && (
                <Box sx={{ ...flexJustifySpaceBetween }}>
                  <Typography fontSize="12px">CLIENT ID:</Typography>
                  <Typography
                    fontSize="12px"
                    component={isLoggedIn ? NavLink : 'span'}
                    to={isLoggedIn ? `/contacts/client/${clientNumber.slice(1, clientNumber.length)}` : undefined}
                    color="primary"
                    variant="subtitle1"
                  >
                    {clientNumber}
                  </Typography>
                </Box>
              )}
              {caseNumber && (
                <Box sx={{ ...flexJustifySpaceBetween }}>
                  <Typography fontSize="12px">MATTER ID:</Typography>
                  <Typography
                    fontSize="12px"
                    component={isLoggedIn ? NavLink : 'span'}
                    to={isLoggedIn ? `/matters/matter-detail/${caseId}` : undefined}
                    color="primary"
                    variant="subtitle1"
                  >
                    #{caseNumber}
                  </Typography>
                </Box>
              )}
              <Box sx={{ ...flexJustifySpaceBetween }}>
                <Typography fontSize="12px">INVOICE NUMBER:</Typography>
                <Typography fontSize="12px">#IN{id}</Typography>
              </Box>
              {dueDate && (
                <Box sx={{ ...flexJustifySpaceBetween }}>
                  <Typography fontSize="12px">DUE DATE:</Typography>
                  <Typography fontSize="12px">{moment(dueDate).format('MMM D, YYYY')}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 2
          }}
        >
          <Typography fontSize="12px" color="text.secondary" fontWeight={500} mb={0.5}>
            MATTER
          </Typography>
          <Typography fontSize="14px" fontWeight={600} color="text.primary">
            {title}
          </Typography>
        </Box>

        <Box
          mb={{ xs: 1.5, md: 3 }}
          sx={{
            overflowX: 'scroll',
            '@media print': {
              overflowX: 'visible !important',
              width: '100% !important'
            }
          }}
        >
          {bill && bill.type === BillType.Bill && (
            <>
              {!isFinalBill && caseId && <ProformaNotes caseId={caseId} />}
              {!!outstandingBillsData?.outstandingBills?.length && (
                <OutstandingBillsTable outstandingBills={outstandingBillsData.outstandingBills} />
              )}
              <BillPreviewEntriesTable billItems={billItems || []} billingType={bill?.case?.billing?.billingType} />
              <BillEntriesSubtotals billItems={billItems || []} isClientView={isClientView} />
            </>
          )}
          {bill && bill.type === BillType.Fund && <FundRequestPreviewEntriesTable bill={bill} />}
        </Box>

        <BillPreviewSummary
          bill={bill}
          isFinalBill={isFinalBill}
          client={clientData}
          clientData={clientDataProp}
          interestData={interestData}
          outstandingBillsData={outstandingBillsData}
          selectedAccessToken={selectedAccessToken}
          onReadyToPrint={onReadyToPrint}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: '#F5F5F5',
          '& *': { fontSize: '12px !important' },
          '@media print': {
            backgroundColor: '#F5F5F5 !important',
            padding: '24px !important',
            marginTop: '24px !important',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
          }
        }}
        p={3}
        mt={3}
        display="inline-block"
        width="100%"
      >
        <Box width="50%" sx={{ float: 'left' }}>
          <Typography>For any questions please contact us at billpay@helmerlegal.com</Typography>
        </Box>

        {showQRCode && (
          <Box
            sx={{
              alignSelf: 'flex-end',
              float: 'right',
              width: '50%',
              textAlign: 'right'
            }}
          >
            <Link href={paymentPageUrl} target="_blank" fontWeight={700} color="darkOne">
              Pay online
            </Link>

            <Box mt={2}>
              <BillQRCode qrValue={paymentPageUrl} />
            </Box>
          </Box>
        )}
      </Box>
    </CardBox>
  )
}

export default BillPreview
